<template>
  <span class="count-to">{{ displayValue }}</span>
</template>

<script>
/**
 * CountTo 数字滚动组件
 * 元素进入视口时,数字从 0 滚动到 endVal
 *
 * 用法:
 *   <CountTo :end-val="1998" :duration="2000" />
 *   <CountTo :end-val="70" :duration="1500" :start-trigger="true" />
 */
export default {
  name: 'CountTo',
  props: {
    // 目标数值
    endVal: {
      type: Number,
      required: true
    },
    // 起始值
    startVal: {
      type: Number,
      default: 0
    },
    // 动画时长(ms)
    duration: {
      type: Number,
      default: 2000
    },
    // 是否自动触发(若为 false,需通过 start-trigger prop 触发)
    autoStart: {
      type: Boolean,
      default: false
    },
    // 外部触发开始(true 时启动)
    startTrigger: {
      type: Boolean,
      default: null
    },
    // 是否使用 requestAnimationFrame 平滑动画
    useEasing: {
      type: Boolean,
      default: true
    },
    // 小数位数
    decimals: {
      type: Number,
      default: 0
    },
    // 千分位分隔符
    separator: {
      type: String,
      default: ''
    },
    // 前缀
    prefix: {
      type: String,
      default: ''
    },
    // 后缀
    suffix: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentVal: this.startVal,
      rafId: null,
      startTime: 0,
      hasStarted: false
    }
  },
  computed: {
    displayValue() {
      const val = this.currentVal.toFixed(this.decimals)
      const parts = val.split('.')
      let intPart = parts[0]
      if (this.separator) {
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.separator)
      }
      const result = parts.length > 1 ? `${intPart}.${parts[1]}` : intPart
      return `${this.prefix}${result}${this.suffix}`
    }
  },
  watch: {
    endVal() {
      this.reset()
      this.start()
    },
    startTrigger(val) {
      if (val === true && !this.hasStarted) {
        this.start()
      }
    }
  },
  mounted() {
    if (this.autoStart) {
      this.start()
    }
  },
  beforeDestroy() {
    this.cancelAnimation()
  },
  methods: {
    start() {
      if (this.hasStarted) return
      this.hasStarted = true
      this.startTime = performance.now()
      this.animate()
    },
    reset() {
      this.cancelAnimation()
      this.hasStarted = false
      this.currentVal = this.startVal
    },
    cancelAnimation() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
    },
    // easeOutQuart 缓动函数,使数字先快后慢
    easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4)
    },
    animate() {
      const now = performance.now()
      const elapsed = now - this.startTime
      const progress = Math.min(elapsed / this.duration, 1)
      const easedProgress = this.useEasing ? this.easeOutQuart(progress) : progress
      const value = this.startVal + (this.endVal - this.startVal) * easedProgress
      this.currentVal = value

      if (progress < 1) {
        this.rafId = requestAnimationFrame(this.animate)
      } else {
        this.currentVal = this.endVal
        this.cancelAnimation()
        this.$emit('end', this.endVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.count-to {
  display: inline-block;
  font-variant-numeric: tabular-nums;
}
</style>
